<template>
  <div class="mc-multi-imei">
    <!-- Company Selection -->
    <div class="mc-multi-imei__company-section mb-3">
      <label class="mc-multi-imei__label">{{ $t('master.multiImei.company') }}</label>
      <FormulateInput
        id="multiImeiCompany"
        name="company"
        v-model="companyId"
        type="select-resourceful-v2"
        :placeholder="$t('master.multiImei.option.placeHolder.company')"
        :optionsGetter="queries.companies"
        :optionLabelFn="
          ({ item }) => {
            return `${item.name} (${item.domain_name})`
          }
        "
        class="pt-0"
        trackBy="company_id"
        :allow-empty="false"
        :initialObject="initialCompany"
        validation="required"
        :validation-messages="{
          required: `${$t('master.multiImei.company')} ${$t('global.required')}`,
        }"
        validation-behavior="live"
        error-behavior="live"
        :fullObject="company"
        @update:fullObject="(val) => (company = val)"
        :disabled="mode !== 'add'"
      />
    </div>

    <!-- Device Parent (GPS) Card -->
    <b-card class="mc-multi-imei__card mc-multi-imei__device-card">
      <div class="mc-multi-imei__device-header">
        <label class="mc-multi-imei__device-label">
          {{ $t('master.multiImei.field.parent') }}
        </label>
      </div>

      <div class="mc-multi-imei__input-group">
        <!-- Select input -->
        <FormulateInput
          name="device_parent"
          v-model="parentVehicleId"
          type="select-resourceful-v2"
          :placeholder="$t('master.multiImei.option.placeHolder.parent')"
          :optionsGetter="queries.parents"
          :optionLabelFn="({ item }) => `${item.license_plate} (${item.imei}) - ${item.device_type_name ?? ''}`"
          class="pt-0"
          trackBy="vehicle_id"
          :allow-empty="false"
          :initialObject="initialParent"
          validation="required"
          :validation-messages="{
            required: `${$t('master.multiImei.field.parent')} ${$t('global.required')}`,
          }"
          validation-behavior="live"
          error-behavior="live"
          :fullObject="vehicle.parent"
          @update:fullObject="(val) => (vehicle.parent = val)"
          :disabled="!company || mode === 'edit-child'"
        />
      </div>

      <!-- Device Parent Details as footer -->
      <template v-if="vehicle.parent" #footer>
        <div class="mc-multi-imei__info-grid">
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__device-code">{{ vehicle.parent.license_plate }}</div>
            <div class="mc-multi-imei__device-name">
              {{ vehicle.parent.vehicle_type_name }}
            </div>
          </div>
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__info-label">{{ $t('master.multiImei.vehicleGroup') }}</div>
            <div class="mc-multi-imei__info-value">
              {{ [vehicle.parent.vgroup_names].filter(Boolean).join(', ') }}
            </div>
          </div>
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__info-label">
              {{ $t('master.multiImei.form.label.infoDevice') }}
            </div>
            <div class="mc-multi-imei__info-value">
              <b-badge class="mc-multi-imei__badge mc-multi-imei__badge--gps">
                {{ $t('master.multiImei.form.label.gps') }}
              </b-badge>
              {{ vehicle.parent.device_type_name }}
            </div>
          </div>
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__info-label">{{ $t('master.multiImei.sensor') }}</div>
            <div class="mc-multi-imei__info-value">
              {{
                vehicle.parent?.sensors?.length
                  ? vehicle.parent.sensors.map((s) => s.sensor_name).join(', ')
                  : '-'
              }}
            </div>
          </div>
        </div>
      </template>
    </b-card>

    <!-- Device Child (Camera) Card -->
    <b-card class="mc-multi-imei__card mc-multi-imei__device-card">
      <div class="mc-multi-imei__device-header">
        <label class="mc-multi-imei__device-label">{{ $t('master.multiImei.field.child') }}</label>
      </div>

      <div class="mc-multi-imei__input-group">
        <FormulateInput
          name="device_child"
          v-model="childVehicleId"
          type="select-resourceful-v2"
          :placeholder="$t('master.multiImei.option.placeHolder.child')"
          :optionsGetter="queries.children"
          :optionLabelFn="
            ({ item }) => {
              return `${item.license_plate} (${item.imei}) - ${item.device_type_name ?? ''}`
            }
          "
          class="pt-0"
          trackBy="vehicle_id"
          :allow-empty="false"
          :initialObject="initialChild"
          validation="required"
          :validation-messages="{
            required: `${$t('master.multiImei.field.child')} ${$t('global.required')}`,
          }"
          validation-behavior="live"
          error-behavior="live"
          :fullObject="vehicle.child"
          @update:fullObject="(val) => (vehicle.child = val)"
          :disabled="!company || mode === 'edit-parent'"
        />
      </div>

      <!-- Device Child Details -->
      <template v-if="vehicle.child" #footer>
        <div class="mc-multi-imei__info-grid">
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__device-code">{{ vehicle.child.license_plate }}</div>
            <div class="mc-multi-imei__device-name">
              {{ vehicle.child.vehicle_type_name }}
            </div>
          </div>
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__info-label">{{ $t('master.multiImei.vehicleGroup') }}</div>
            <div class="mc-multi-imei__info-value">
              {{ [vehicle.child.vgroup_names].filter(Boolean).join(', ') }}
            </div>
          </div>
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__info-label">
              {{ $t('master.multiImei.form.label.infoDevice') }}
            </div>
            <div class="mc-multi-imei__info-value">
              <b-badge class="mc-multi-imei__badge mc-multi-imei__badge--camera">
                {{ $t('master.multiImei.form.label.camera') }}
              </b-badge>
              {{ vehicle.child.device_type_name }}
            </div>
          </div>
          <div class="mc-multi-imei__info-col">
            <div class="mc-multi-imei__info-label">{{ $t('master.multiImei.sensor') }}</div>
            <div class="mc-multi-imei__info-value">
              {{
                vehicle.child?.sensors?.length
                  ? vehicle.child.sensors.map((s) => s.sensor_name).join(', ')
                  : '-'
              }}
            </div>
          </div>
        </div>
      </template>
    </b-card>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import * as companyApi from '@/services/rest-api-sms/lib/company/company'
import * as vehicleApi from '@/services/rest-api-sms/lib/vehicle/vehicle'

export default {
  name: 'MultiImeiForm',

  props: {
    data: { type: Object, default: null }, // data untuk edit
    mode: { type: String, default: 'add' }, // 'add' atau 'edit'
    serverRegion: { type: String, default: 'SG' },
  },

  watch: {
    data: {
      handler(val) {
        if (!val) {
          this.vehicle = { parent: null, child: null }
          this.company = null
          return
        }
        const { parent_vehicle, ...child_vehicle } = val
        this.vehicle.parent = parent_vehicle
        this.vehicle.child = child_vehicle
        this.company = this.vehicle
          ? {
              company_id: this.vehicle.parent.company_id,
              domain_name: this.vehicle.parent.domain_name,
              name: this.vehicle.parent.company_name,
            }
          : null
        this.initialCompany = this.company
        this.initialParent = this.vehicle.parent
        this.initialChild = this.vehicle.child

        // v-model
        this.companyId = this.initialCompany.company_id
        this.parentVehicleId = this.initialParent.vehicle_id
        this.childVehicleId = this.initialChild.vehicle_id
      },
      immediate: true,
    },

    company(newVal, oldVal) {
      if (newVal && oldVal && newVal.company_id !== oldVal.company_id) {
        // reset parent & child
        this.vehicle.parent = null
        this.vehicle.child = null
        this.parentVehicleId = null
        this.childVehicleId = null
        this.initialParent = null
        this.initialChild = null
      }

      this.initialCompany = newVal
      this.companyId = newVal.company_id
      this.$emit('update-selection', {
        parent: this.vehicle.parent,
        child: this.vehicle.child,
      })
      this.$emit('validated', !this.$v.$invalid)
    },

    async 'vehicle.parent'(val) {
      this.initialParent = val
      this.parentVehicleId = val.vehicle_id

      // Ambil list children dari API
      if (this.mode === 'add') {
        const child = await this.queries.children({
          page: 1,
          limit: 1,
          filters: [`license_plate eq '${val.license_plate} - CAM'`],
        })
        if (child?.data?.paginated_result?.[0]) {
          this.vehicle.child = child.data.paginated_result[0]
          this.initialChild = this.vehicle.child
          this.childVehicleId = this.vehicle.child.vehicle_id
        }
      }

      this.$emit('update-selection', {
        parent: val,
        child: this.vehicle.child,
      })
      this.$emit('validated', !this.$v.$invalid)
    },

    async 'vehicle.child'(val) {
      this.initialChild = val
      this.childVehicleId = val.vehicle_id

      const licensePlate = val.license_plate

      // Cek apakah ada suffix '- CAM'
      if (this.mode === 'add') {
        if (licensePlate.endsWith(' - CAM')) {
          const parentLicensePlate = licensePlate.replace(' - CAM', '').trim()

          const parent = await this.queries.parents({
            page: 1,
            limit: 1,
            filters: [`license_plate eq '${parentLicensePlate}'`],
          })

          if (parent?.data?.paginated_result?.[0]) {
            this.vehicle.parent = parent.data.paginated_result[0]
            this.initialParent = this.vehicle.parent
            this.parentVehicleId = this.vehicle.parent.vehicle_id
          }
        }
      }

      this.$emit('update-selection', {
        parent: this.vehicle.parent,
        child: val,
      })
      this.$emit('validated', !this.$v.$invalid)
    },
  },

  data() {
    return {
      company: null,
      companyOptions: [],
      vehicle: {
        parent: null,
        child: null,
      },
      pagination: {
        page: 1,
        limit: 10,
        search: null,
      },
      initialCompany: null,
      initialParent: null,
      initialChild: null,
      companyId: null,
      parentVehicleId: null,
      childVehicleId: null,
    }
  },

  validations: {
    vehicle: {
      child: { required },
      parent: { required },
    },
    company: { required },
  },

  computed: {
    queries() {
      return {
        companies: async (opts) => {
          const params = {
            search: opts?.search,
            limit: opts?.limit,
            page: opts?.page,
            filters: opts?.filters,
          }

          const response = await companyApi.get(params)

          if (this.initialCompany) {
            const exists = response.data.paginated_result.some(
              (company) => company.company_id === this.initialCompany.company_id
            )

            if (opts?.page === 1) {
              // kalau di page 1, pastikan initialCompany ada di index 0
              if (exists) {
                // kalau sudah ada, hapus dulu biar ga dobel
                response.data.paginated_result = response.data.paginated_result.filter(
                  (company) => company.company_id !== this.initialCompany.company_id
                )
                response.metadata.count -= 1
              }

              // taruh di depan list
              response.data.paginated_result.unshift(this.initialCompany)
              response.metadata.count += 1
            } else {
              // kalau bukan page 1, pastikan initialCompany tidak ada di list
              if (exists) {
                response.data.paginated_result = response.data.paginated_result.filter(
                  (company) => company.company_id !== this.initialCompany.company_id
                )
                response.metadata.count -= 1
              }
            }
          }

          return response
        },

        parents: async (opts) => {
          if (!this.company) return { data: [], total: 0 }

          const filters = [
            `device_category eq GPS`,
            `imei neq ''`,
            `imei neq ' '`,
            `imei is not null`,
            'child_vehicle_not_exist',
            'parent_vehicle_id is null',
            `company_id eq ${this.company.company_id}`,
          ]

          // kalau ada tambahan filter dari opts, append aja
          if (opts?.filters?.length) {
            filters.push(...opts.filters)
          }

          const params = {
            search: opts?.search,
            limit: opts?.limit,
            page: opts?.page,
            filters,
          }

          const response = await vehicleApi.get(params)

          if (this.initialParent) {
            const exists = response.data.paginated_result.some(
              (vehicle) => vehicle.vehicle_id === this.initialParent.vehicle_id
            )

            if (opts?.page === 1) {
              // kalau di page 1, pastikan initialParent ada di index 0
              if (exists) {
                // kalau sudah ada, hapus dulu biar ga dobel
                response.data.paginated_result = response.data.paginated_result.filter(
                  (vehicle) => vehicle.vehicle_id !== this.initialParent.vehicle_id
                )
                response.metadata.count -= 1
              }

              // taruh di depan list
              response.data.paginated_result.unshift(this.initialParent)
              response.metadata.count += 1
            } else {
              // kalau bukan page 1, pastikan initialParent tidak ada di list
              if (exists) {
                response.data.paginated_result = response.data.paginated_result.filter(
                  (vehicle) => vehicle.vehicle_id !== this.initialParent.vehicle_id
                )
                response.metadata.count -= 1
              }
            }
          }

          return response
        },

        children: async (opts) => {
          if (!this.company) return { data: [], total: 0 }

          const filters = [
            `device_category eq CAM`,
            `imei neq ''`,
            `imei neq ' '`,
            `imei is not null`,
            'child_vehicle_not_exist',
            'parent_vehicle_id is null',
            `company_id eq ${this.company.company_id}`,
          ]

          // kalau ada tambahan filter dari opts, append aja
          if (opts?.filters?.length) {
            filters.push(...opts.filters)
          }

          const params = {
            search: opts?.search,
            limit: opts?.limit,
            page: opts?.page,
            filters,
          }

          const response = await vehicleApi.get(params)

          if (this.initialChild) {
            const exists = response.data.paginated_result.some(
              (vehicle) => vehicle.vehicle_id === this.initialChild.vehicle_id
            )

            if (opts?.page === 1) {
              // kalau di page 1, pastikan initialChild ada di index 0
              if (exists) {
                // kalau sudah ada, hapus dulu biar ga dobel
                response.data.paginated_result = response.data.paginated_result.filter(
                  (vehicle) => vehicle.vehicle_id !== this.initialChild.vehicle_id
                )
                response.metadata.count -= 1
              }

              // taruh di depan list
              response.data.paginated_result.unshift(this.initialChild)
              response.metadata.count += 1
            } else {
              // kalau bukan page 1, pastikan initialChild tidak ada di list
              if (exists) {
                response.data.paginated_result = response.data.paginated_result.filter(
                  (vehicle) => vehicle.vehicle_id !== this.initialChild.vehicle_id
                )
                response.metadata.count -= 1
              }
            }
          }

          return response
        },
      }
    },
  },

  methods: {
    customLabelCompany(item) {
      return `${item.name} (${item.domain_name})`
    },

    customLabelParent(item) {
      return `${item.imei}`
    },

    customLabelChild(item) {
      return `${item.imei}`
    },
  },
}
</script>
